import { useEffect, useRef, useState } from "react";

import { Box, Flex, Grid, Heading, Link, Spinner, Text, useColorModeValue, useToast } from "@chakra-ui/react";
import { useUser } from "@clerk/clerk-react";
import { PhCalculationRequest, PhCalculationResponse } from "@henophess/types";
import { Link as RouterLink } from "react-router-dom";

import { fetchGroupMembershipId } from "~/api/members";
import { AuthenticationRequiredError, savePhData, submitPhCalculation } from "~/api/ph";
import { PhCalculationInputForm, PhCalculationInputFormRef } from "~/components/tools/PhCalculationInputForm";
import { PhMeasurementsChart } from "~/components/tools/PhChart";
import { PhRecommendationsDisplay } from "~/components/tools/PhRecommendationsDisplay";
import { PhTestControls } from "~/components/tools/PhTestControls";
import { SymptomsBox } from "~/components/tools/SymptomsBox";
import { useAppState } from "~/hooks/useAppState";
import { dispatchPhAnalysisCompleted } from "~/utils/cacheInvalidation";

export const PhAnalysisTab = () => {
  const [analysis, setAnalysis] = useState<PhCalculationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isTestMode, setIsTestMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const formRef = useRef<PhCalculationInputFormRef>(null);
  const toast = useToast();
  const { isSignedIn, isLoaded } = useUser();
  const { currentMember, currentGroupMember } = useAppState("currentMember", "currentGroupMember");

  // Color mode values
  const errorColor = useColorModeValue("red.500", "red.300");

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "t" && !event.repeat && document.activeElement?.tagName !== "INPUT") {
        setIsTestMode((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  const handleSubmit = async (data: PhCalculationRequest) => {
    try {
      const result = await submitPhCalculation(data);
      setAnalysis(result as PhCalculationResponse);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  const handleSave = async (data: PhCalculationRequest) => {
    // First check if user is signed in to provide a better UX
    if (isLoaded && !isSignedIn) {
      toast({
        title: "Login Required",
        description: (
          <>
            Please{" "}
            <Link as={RouterLink} to="/signin" color="blue.500" fontWeight="bold">
              log in
            </Link>{" "}
            to save your pH data
          </>
        ),
        status: "warning",
        duration: 5000,
        isClosable: true
      });
      setError("Please log in to save your pH data");
      return;
    }

    // Check if a member is selected
    if (!currentMember) {
      toast({
        title: "No Member Selected",
        description: "Please select a member to save pH data",
        status: "warning",
        duration: 5000,
        isClosable: true
      });
      setError("Please select a member to save pH data");
      return;
    }

    try {
      setLoading(true);

      // Get the group_members_id, either from currentGroupMember or by fetching it
      let groupMemberId: number | null = null;

      if (currentGroupMember) {
        // Use the already available group member ID
        groupMemberId = currentGroupMember.id;
      } else {
        // Fetch the group membership ID if currentGroupMember is not available
        groupMemberId = await fetchGroupMembershipId(currentMember.id);
      }

      if (!groupMemberId) {
        toast({
          title: "Error",
          description: "Could not find group membership for this member",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        setError("Could not find group membership for this member");
        setLoading(false);
        return;
      }

      // Call the savePhData function with the calculation request and groupMemberId
      const result = await savePhData(data, groupMemberId);

      // Set the analysis results, just like when calculating
      setAnalysis(result as PhCalculationResponse);

      // Dispatch pH analysis completed event to invalidate member recommendations cache
      dispatchPhAnalysisCompleted(groupMemberId);

      // Check if the result contains a duplicate flag
      if (result.duplicate) {
        // Show duplicate warning toast
        // @todo, get the time interval dynamically from the server
        toast({
          title: "Note: Similar data already exists",
          description: result.message || "Similar pH data was already saved within ±2 hours of the testing date",
          status: "info",
          duration: 5000,
          isClosable: true
        });
      } else {
        // Show success toast
        toast({
          title: `pH data saved successfully!`,
          status: "success",
          duration: 3000,
          isClosable: true
        });
      }

      setError(null);
    } catch (err) {
      // Special handling for auth errors
      if (err instanceof AuthenticationRequiredError) {
        toast({
          title: "Login Required",
          description: (
            <>
              Please{" "}
              <Link as={RouterLink} to="/signin" color="blue.500" fontWeight="bold">
                log in
              </Link>{" "}
              to save your pH data
            </>
          ),
          status: "warning",
          duration: 5000,
          isClosable: true
        });
        setError("Please log in to save your pH data");
      } else {
        // General error handling
        toast({
          title: "Error saving pH data",
          description: err instanceof Error ? err.message : "An error occurred",
          status: "error",
          duration: 5000,
          isClosable: true
        });
        setError(err instanceof Error ? err.message : "An error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTestData = (data: PhCalculationRequest) => {
    if (formRef.current) {
      formRef.current.populate(data);
    }
  };

  // If no member is selected, show a message to select a member
  if (!currentMember) {
    return (
      <Box p={4} textAlign="center">
        <Text>Please select a member from the dropdown above to use pH Analysis.</Text>
      </Box>
    );
  }

  // If initializing, show loading spinner
  if (initializing) {
    return (
      <Box p={4} textAlign="center">
        <Spinner size="xl" color="green.500" mb={4} />
        <Text>Loading pH Analysis...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="center" p={4} direction="column" align="center" gap={6}>
        <Heading as="h2" size="lg">
          pH Analysis
        </Heading>
        {isTestMode && <PhTestControls onTestDataGenerated={handleTestData} showTrendControls={false} />}
      </Flex>

      <Box width="100%">
        <PhCalculationInputForm
          onSubmit={handleSubmit}
          onSave={isSignedIn && currentMember ? handleSave : undefined}
          ref={formRef}
          isLoading={loading}
        />
      </Box>

      {error && (
        <Text color={errorColor} fontSize="sm" mt={4}>
          {error}
        </Text>
      )}

      {analysis && (
        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={8} alignItems="start" mt={8}>
          <PhRecommendationsDisplay
            supplementRecommendations={analysis.supplementRecommendations || []}
            textRecommendations={analysis.textResponses || []}
          />
          <PhMeasurementsChart analysis={analysis} />
        </Grid>
      )}

      {analysis && (
        <Box width="100%" mt={8}>
          <Grid templateColumns={{ base: "1fr", md: "1fr" }} gap={4}>
            <Box>
              <SymptomsBox symptoms={analysis.symptoms} />
            </Box>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default PhAnalysisTab;
